import { getPrettyDateTime } from "../../../../../../utils/timestamp";
import { IntegrationsMapping } from "../../../../integrations/IntegrationsMapping";

export const getUserConnectedHostsRows = (entityData, hostEntities) => {
  let rowsMap = [];

  if (entityData?.hosts_ids) {
    for (const hostId of entityData.hosts_ids) {
      const hostEntity = hostEntities?.find(
        (entity) => entity.unique_id === hostId
      );
      const hostSources = hostEntity?.sources;
      rowsMap.push({
        id: rowsMap.length + 1,
        hostId: hostId,
        entityObj: hostEntity,
        source: hostSources ? hostSources : [],
      });
    }
  }

  return rowsMap;
};

export const getUserRelatedTokensRows = (
  entityData,
  nonhumanIdentityEntities
) => {
  let rowsMap = [];

  if (entityData?.related_token_ids) {
    for (const tokenId of entityData.related_token_ids) {
      const tokenEntity = nonhumanIdentityEntities?.find(
        (entity) => entity.unique_id === tokenId
      );
      const tokenSources = tokenEntity?.sources;
      rowsMap.push({
        id: rowsMap.length + 1,
        tokenId: tokenId,
        entityObj: tokenEntity,
        source: tokenSources ? tokenSources : [],
      });
    }
  }

  return rowsMap;
};

const createAppRow = (appEntity, rowId) => {
  const appSources = appEntity?.sources;
  return {
    id: rowId,
    appId: appEntity.unique_id,
    name: appEntity?.display_name || "Unknown",
    entityObj: appEntity,
    source: appSources ? appSources : [],
  };
};

const getUserGoogleAppIDs = (userEntityId, appEntities) => {
  if (!userEntityId || !appEntities) return [];
  // Filter apps where google_data.user_ids includes userEntityId
  const googleRelatedApps = appEntities.filter(
    (appEntity) =>
      Array.isArray(appEntity?.google_data?.user_ids) &&
      appEntity.google_data.user_ids.includes(userEntityId)
  );
  const googleRelatedAppIDs = googleRelatedApps.map(
    (appEntity) => appEntity.unique_id
  );
  return googleRelatedAppIDs;
};

export const getUserRelatedAppsRows = (entityData, appEntities) => {
  let rowsMap = [];
  if (!entityData || !appEntities) return rowsMap;

  const userEntityId = entityData?.entity_id;
  const userRelatedAppIDs = [
    ...(entityData?.okta_data?.okta_app_unique_ids || []),
    ...(entityData?.salesforce_data?.created_app_unique_ids || []),
    ...(getUserGoogleAppIDs(userEntityId, appEntities) || []),
  ];
  if (userRelatedAppIDs.length > 0) {
    for (const appId of userRelatedAppIDs) {
      const appEntity = appEntities?.find(
        (entity) => entity.unique_id === appId
      );
      if (appEntity) {
        rowsMap.push(createAppRow(appEntity, rowsMap.length + 1));
      }
    }
  }
  return rowsMap;
};

export const getUserLastPasswordChangeRows = (entityData) => {
  let rowsMap = [];

  if (entityData?.okta_data?.password_last_changed_date) {
    rowsMap.push({
      id: 1,
      last_time_changed_password:
        entityData?.okta_data?.password_last_changed_date,
      source: "okta",
    });
  }
  if (entityData?.snowflake_data?.password_last_set_time) {
    rowsMap.push({
      id: 2,
      last_time_changed_password:
        entityData?.snowflake_data?.password_last_set_time,
      source: "snowflake",
    });
  }
  if (entityData?.jamf_data?.last_password_change) {
    rowsMap.push({
      id: 3,
      last_time_changed_password: entityData?.jamf_data?.last_password_change,
      source: "jamf",
    });
  }
  if (entityData?.jumpcloud_data?.last_time_changed_password) {
    rowsMap.push({
      id: 4,
      last_time_changed_password:
        entityData?.jumpcloud_data?.last_time_changed_password,
      source: "jumpcloud",
    });
  }
  if (entityData?.microsoft_entra_id_data?.password_created_date) {
    rowsMap.push({
      id: 5,
      last_time_changed_password:
        entityData?.microsoft_entra_id_data?.password_created_date,
      source: "microsoft_entra_id",
    });
  }
  if (entityData?.salesforce_data?.last_password_change_date) {
    rowsMap.push({
      id: 6,
      last_time_changed_password:
        entityData?.salesforce_data?.last_password_change_date,
      source: IntegrationsMapping.salesforce.codeName,
    });
  }
  if (entityData?.microsoft_active_directory_data?.last_password_change_date) {
    rowsMap.push({
      id: 7,
      last_time_changed_password:
        entityData?.microsoft_active_directory_data?.last_password_change_date,
      source: IntegrationsMapping.microsoft_active_directory.codeName,
    });
  }
  return rowsMap;
};

export const getUserLastSeenRows = (entityData) => {
  let rowsMap = [];
  let rowIdCounter = 1;

  if (entityData?.last_seen && typeof entityData.last_seen === "object") {
    Object.entries(entityData.last_seen).forEach(
      ([platform, timestampValue]) => {
        let lastSeenValue;
        if (typeof timestampValue === "number" && timestampValue > 0) {
          lastSeenValue = {
            timestamp_utc: timestampValue,
            timestamp_utc_human_readable: new Date(
              timestampValue * 1000
            ).toISOString(),
          };
        } else if (
          typeof timestampValue === "object" &&
          timestampValue?.timestamp_utc
        ) {
          lastSeenValue = timestampValue;
        } else {
          lastSeenValue = "Never";
        }

        rowsMap.push({
          id: rowIdCounter++,
          last_seen:
            typeof lastSeenValue === "object" && lastSeenValue?.timestamp_utc
              ? getPrettyDateTime(lastSeenValue.timestamp_utc)
              : "Never",
          source: platform,
        });
      }
    );
  }

  return rowsMap;
};

export const getUserCreationDateRows = (entityData) => {
  let rowsMap = [];

  if (entityData?.google_data?.creation_date) {
    rowsMap.push({
      id: 1,
      creation_date: entityData?.google_data?.creation_date,
      source: "google",
    });
  }
  if (entityData?.okta_data?.creation_date) {
    rowsMap.push({
      id: 2,
      creation_date: entityData?.okta_data?.creation_date,
      source: "okta",
    });
  }
  if (entityData?.mongo_atlas_data?.creation_date) {
    rowsMap.push({
      id: 3,
      creation_date: entityData?.mongo_atlas_data?.creation_date,
      source: "mongoatlas",
    });
  }
  if (entityData?.paloalto_talon_data?.first_seen) {
    rowsMap.push({
      id: 4,
      creation_date:
        entityData?.paloalto_talon_data?.first_seen.timestamp_utc > 0
          ? entityData?.paloalto_talon_data?.first_seen
          : "Never",
      source: "paloalto_talon",
    });
  }
  if (entityData?.paloalto_prisma_access_browser_data?.first_seen) {
    rowsMap.push({
      id: 5,
      creation_date:
        entityData?.paloalto_prisma_access_browser_data?.first_seen
          .timestamp_utc > 0
          ? entityData?.paloalto_prisma_access_browser_data?.first_seen
          : "Never",
      source: "paloalto_prisma_access_browser",
    });
  }
  if (entityData?.microsoft_office_365_data?.creation_date) {
    rowsMap.push({
      id: 6,
      creation_date: entityData?.microsoft_office_365_data?.creation_date,
      source: "microsoft_office_365",
    });
  }
  if (entityData?.github_data?.creation_date) {
    rowsMap.push({
      id: 7,
      creation_date: entityData.github_data.creation_date,
      source: "github",
    });
  }
  if (entityData?.snowflake_data?.creation_date) {
    rowsMap.push({
      id: 8,
      creation_date: entityData?.snowflake_data?.creation_date,
      source: "snowflake",
    });
  }
  if (entityData?.atlassian_data?.first_seen) {
    rowsMap.push({
      id: 9,
      creation_date: entityData?.atlassian_data?.first_seen,
      source: "atlassian",
    });
  }
  if (entityData?.zoom_data?.creation_date) {
    rowsMap.push({
      id: 10,
      creation_date: entityData?.zoom_data?.creation_date,
      source: "zoom",
    });
  }

  if (entityData?.starburst_data?.created_at) {
    rowsMap.push({
      id: 11,
      creation_date: entityData?.starburst_data?.created_at,
      source: "starburst",
    });
  }
  if (entityData?.netskope_data?.first_seen) {
    rowsMap.push({
      id: 12,
      creation_date: entityData?.netskope_data?.first_seen,
      source: "netskope",
    });
  }
  if (entityData?.zendesk_data?.first_seen) {
    rowsMap.push({
      id: 13,
      creation_date: entityData?.zendesk_data?.first_seen,
      source: "zendesk",
    });
  }
  if (entityData?.box_data?.created_at) {
    rowsMap.push({
      id: 14,
      creation_date: entityData?.box_data?.created_at,
      source: "box",
    });
  }
  if (entityData?.cloudflare_ztna_data?.created_at) {
    rowsMap.push({
      id: 15,
      creation_date: entityData?.cloudflare_ztna_data?.created_at,
      source: "cloudflare",
    });
  }
  if (entityData?.jumpcloud_data?.creation_date) {
    rowsMap.push({
      id: 16,
      creation_date: entityData?.jumpcloud_data?.creation_date,
      source: "jumpcloud",
    });
  }
  if (entityData?.kandji_data?.creation_date) {
    rowsMap.push({
      id: 17,
      creation_date: entityData?.kandji_data?.creation_date,
      source: "kandji",
    });
  }
  if (entityData?.gitlab_data?.created_at) {
    rowsMap.push({
      id: 18,
      creation_date: entityData?.gitlab_data?.created_at,
      source: "gitlab",
    });
  }
  if (entityData?.monday_data?.created_at) {
    rowsMap.push({
      id: 19,
      creation_date: entityData?.monday_data?.created_at,
      source: "monday",
    });
  }
  if (entityData?.crowdstrike_data?.created_at) {
    rowsMap.push({
      id: 20,
      creation_date: entityData?.crowdstrike_data?.created_at,
      source: "crowdstrike",
    });
  }
  if (entityData?.slack_saas_data?.user_created_date) {
    rowsMap.push({
      id: 21,
      creation_date: entityData?.slack_saas_data?.user_created_date,
      source: "slack_saas",
    });
  }
  if (entityData?.servicenow_cmdb_data?.created_at) {
    rowsMap.push({
      id: 22,
      creation_date: entityData?.servicenow_cmdb_data?.created_at,
      source: "servicenow_cmdb",
    });
  }
  if (entityData?.cato_data?.creation_date) {
    rowsMap.push({
      id: 22,
      creation_date: entityData?.cato_data?.creation_date,
      source: "cato",
    });
  }
  if (entityData?.salesforce_data?.created_date) {
    rowsMap.push({
      id: 23,
      creation_date: entityData?.salesforce_data?.created_date,
      source: IntegrationsMapping.salesforce.codeName,
    });
  }
  if (entityData?.microsoft_active_directory_data?.creation_date) {
    rowsMap.push({
      id: 27,
      creation_date: entityData?.microsoft_active_directory_data?.creation_date,
      source: IntegrationsMapping.microsoft_active_directory.codeName,
    });
  }
  if (entityData?.claude_data?.added_at) {
    rowsMap.push({
      id: 28,
      creation_date: entityData?.claude_data?.added_at,
      source: IntegrationsMapping.claude.codeName,
    });
  }
  if (entityData?.openai_data?.added_at) {
    rowsMap.push({
      id: 29,
      creation_date: entityData?.openai_data?.added_at,
      source: "openai",
    });
  }
  if (entityData?.imperva_waf_data?.creation_date) {
    rowsMap.push({
      id: 30,
      creation_date: entityData?.imperva_waf_data?.creation_date,
      source: "imperva_waf",
    });
  }
  return rowsMap;
};

export const getUserLastUpdateDateRows = (entityData) => {
  let rowsMap = [];

  if (entityData?.github_data?.last_updated_date) {
    rowsMap.push({
      id: 1,
      last_updated: entityData.github_data.last_updated_date,
      source: "github",
    });
  }
  if (entityData?.box_data?.modified_at) {
    rowsMap.push({
      id: 2,
      last_updated: entityData?.box_data?.modified_at,
      source: "box",
    });
  }
  if (entityData?.kandji_data?.last_updated_date) {
    rowsMap.push({
      id: 3,
      last_updated: entityData?.kandji_data?.last_updated_date,
      source: "kandji",
    });
  }
  if (entityData?.crowdstrike_data?.last_modified_at) {
    rowsMap.push({
      id: 4,
      last_updated: entityData?.crowdstrike_data?.last_modified_at,
      source: "crowdstrike",
    });
  }
  if (entityData?.servicenow_cmdb_data?.last_updated) {
    rowsMap.push({
      id: 5,
      last_updated: entityData?.servicenow_cmdb_data?.last_updated,
      source: "servicenow_cmdb",
    });
  }
  if (entityData?.microsoft_active_directory_data?.last_change_date) {
    rowsMap.push({
      id: 6,
      last_updated:
        entityData?.microsoft_active_directory_data?.last_change_date,
      source: IntegrationsMapping.microsoft_active_directory.codeName,
    });
  }
  if (entityData?.cato_data?.last_modified_date) {
    rowsMap.push({
      id: 7,
      last_updated: entityData?.cato_data?.last_modified_date,
      source: "cato",
    });
  }
  return rowsMap;
};

const getFactorValue = (factor) => {
  const factorDictionary = {
    hotp: factor.vendor_name + " Hardware OTP",
    call: "Voice call",
    email: "Email authentication",
    push: factor.vendor_name + " Push",
    question: "Security Questions",
    sms: "SMS authentication",
    token: factor.vendor_name + " token",
    "token:hardware": factor.vendor_name + " Hardware token",
    "token:software:totp": factor.vendor_name + " Software token",
    u2f: factor.vendor_name + " Universal 2nd factor",
    web: "Vendor name",
    webauthn: factor.vendor_name + " FIDO2",
    signed_nonce: factor.vendor_name + " FastPass",
  };

  return factorDictionary[factor.factor_type] || factor.factor_type;
};

export const getUserMFARows = (entityData) => {
  let rowsMap = [];

  if (entityData?.google_data?.is_enforced_in_2sv != null) {
    rowsMap.push({
      id: rowsMap.length,
      enforced: entityData?.google_data?.is_enforced_in_2sv ? "Yes" : "No",
      source: "google",
    });
  }
  if (entityData?.cloudflare_data?.two_factor_authentication != null) {
    rowsMap.push({
      id: rowsMap.length,
      enforced: entityData?.cloudflare_data?.two_factor_authentication
        ? "Yes"
        : "No",
      source: "cloudflare",
    });
  }
  if (entityData?.snowflake_data?.is_mfa_enabled != null) {
    rowsMap.push({
      id: rowsMap.length,
      enforced: entityData?.snowflake_data?.is_mfa_enabled ? "Yes" : "No",
      source: "snowflake",
    });
  }
  entityData?.okta_data?.factors?.map((factor, index) => {
    rowsMap.push({
      id: rowsMap.length + index,
      factor_type: getFactorValue(factor),
      last_updated: factor.last_updated,
      source: "okta",
    });
  });
  entityData?.microsoft_entra_id_data?.auth_methods_full_details?.map(
    (factor, index) => {
      rowsMap.push({
        id: rowsMap.length + index,
        factor_type: factor?.method_type || factor,
        source: "microsoft_entra_id",
      });
    }
  );
  if (entityData?.bitwarden_data?.is_two_factor_enabled != null) {
    rowsMap.push({
      id: rowsMap.length,
      enabled: entityData?.bitwarden_data?.is_two_factor_enabled ? "Yes" : "No",
      source: "bitwarden",
    });
  }
  if (entityData?.jumpcloud_data?.mfa_enrolled != null) {
    rowsMap.push({
      id: rowsMap.length,
      enforced: entityData?.jumpcloud_data?.mfa_enforced ? "Yes" : "No",
      enabled: entityData?.jumpcloud_data?.mfa_enrolled ? "Yes" : "No",
      factor_type: "JumpCloud",
      last_updated: entityData?.jumpcloud_data?.mfa_last_update_date,
      source: "jumpcloud",
    });
  }
  if (entityData?.servicenow_cmdb_data?.enable_multifactor_auth != null) {
    rowsMap.push({
      id: rowsMap.length,
      enabled: entityData?.servicenow_cmdb_data?.enable_multifactor_auth
        ? "Yes"
        : "No",
      source: "servicenow_cmdb",
    });
  }
  if (entityData?.cato_data?.mfa_enabled != null) {
    rowsMap.push({
      id: rowsMap.length,
      enabled: entityData?.cato_data?.mfa_enabled ? "Yes" : "No",
      source: "cato",
    });
  }

  return rowsMap;
};

export const getUserPoliciesRows = (entityData) => {
  let rowsMap = [];

  if (entityData?.crowdstrike_data?.policies) {
    for (const policy of entityData.crowdstrike_data.policies) {
      rowsMap.push({
        id: rowsMap.length + 1,
        policy_name: policy.policy_name,
        policy_type: policy.policy_type,
        source: "crowdstrike",
      });
    }
  }

  if (entityData?.okta_data?.policies) {
    for (const policy of entityData.okta_data.policies) {
      rowsMap.push({
        id: rowsMap.length + 1,
        policy_name: policy.policy_name,
        policy_type: policy.policy_type,
        source: "okta",
      });
    }
  }

  if (entityData?.aws_data?.policies) {
    for (const policy of entityData.aws_data.policies) {
      rowsMap.push({
        id: rowsMap.length + 1,
        policy_name: policy.policy_name,
        policy_type: policy.policy_type,
        source: "aws",
      });
    }
  }

  return rowsMap;
};

export const getUserGroupsRows = (entityData) => {
  let rowsMap = [];

  if (entityData?.okta_data?.groups) {
    for (const group of entityData.okta_data.groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group?.name,
        group_type: group?.group_type,
        source: "okta",
      });
    }
  }

  if (entityData?.microsoft_entra_id_data?.groups) {
    for (const group of entityData.microsoft_entra_id_data.groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: "microsoft_entra_id",
      });
    }
  }
  if (entityData?.paloalto_talon_data?.user_groups) {
    for (const group of entityData.paloalto_talon_data.user_groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group.name,
        group_type: group.provider,
        source: "paloalto_talon",
      });
    }
  }
  if (entityData?.zoom_data?.group_names) {
    for (const group of entityData?.zoom_data?.group_names) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: "zoom",
      });
    }
  }
  if (entityData?.netskope_data?.user_groups) {
    for (const group of entityData?.netskope_data?.user_groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: "netskope",
      });
    }
  }
  if (entityData?.jumpcloud_data?.groups) {
    for (const group of entityData?.jumpcloud_data?.groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: "jumpcloud",
      });
    }
  }
  if (entityData?.gitlab_data?.groups) {
    for (const group of entityData?.gitlab_data?.groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: "gitlab",
      });
    }
  }
  if (entityData?.monday_data?.teams) {
    for (const team of entityData?.monday_data?.teams) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: team,
        source: "monday",
      });
    }
  }
  if (entityData?.cloudconnexa_open_vpn_data?.groups) {
    for (const group of entityData?.cloudconnexa_open_vpn_data?.groups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: "cloudconnexa_open_vpn",
      });
    }
  }

  if (
    entityData?.microsoft_active_directory_data?.groups ||
    entityData?.microsoft_active_directory_data?.known_privileged_groups
  ) {
    const adGroups = [
      ...(entityData?.microsoft_active_directory_data?.groups || []),
      ...(entityData?.microsoft_active_directory_data
        ?.known_privileged_groups || []),
    ];
    const uniqueAdGroups = Array.from(new Set(adGroups));
    for (const group of uniqueAdGroups) {
      rowsMap.push({
        id: rowsMap.length + 1,
        group_name: group,
        source: IntegrationsMapping.microsoft_active_directory.codeName,
      });
    }
  }

  return rowsMap;
};

export const getUserRolesRows = (entityData) => {
  let rowsMap = [];

  if (entityData?.cursor_data?.role) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData.cursor_data.role,
      source: "cursor",
    });
  }

  if (entityData?.microsoft_entra_id_data?.roles) {
    for (const role of entityData.microsoft_entra_id_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "microsoft_entra_id",
      });
    }
  }

  if (entityData?.admin_roles?.okta) {
    const rolesSet = new Set();

    for (const role of entityData.admin_roles.okta) {
      if (!rolesSet.has(role)) {
        rolesSet.add(role);
        rowsMap.push({
          id: rowsMap.length + 1,
          role_name: role,
          source: "okta",
        });
      }
    }
  }

  if (entityData?.mongo_atlas_data?.roles) {
    for (const role of entityData.mongo_atlas_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role.name,
        source: "mongoatlas",
      });
    }
  }

  if (entityData?.cloudflare_data?.roles) {
    for (const role of entityData.cloudflare_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "cloudflare",
      });
    }
  }

  if (entityData?.snowflake_data?.user_roles) {
    for (const role of entityData.snowflake_data.user_roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "snowflake",
      });
    }
  }

  if (entityData?.salesforce_data?.profile_name) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData.salesforce_data.profile_name,
      source: IntegrationsMapping.salesforce.codeName,
    });
  }

  if (entityData?.google_data?.user_roles) {
    for (const role of entityData.google_data.user_roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "google",
      });
    }
  }

  if (entityData?.microsoft_office_365_data?.user_roles) {
    for (const role of entityData.microsoft_office_365_data?.user_roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "microsoft_office_365",
      });
    }
  }

  if (entityData?.jamf_data?.privilege_level) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData?.jamf_data?.privilege_level,
      source: "jamf",
    });
  }

  if (entityData?.breeze_data?.role) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData.breeze_data.role,
      source: "breeze",
    });
  }

  if (entityData?.github_data?.roles) {
    for (const role of entityData.github_data?.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "github",
      });
    }
  }

  if (entityData?.admin_roles?.slack_saas?.length > 0) {
    for (const role of entityData.admin_roles.slack_saas) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "slack_saas",
      });
    }
  }

  if (entityData?.admin_roles?.cato?.length > 0) {
    for (const role of entityData.admin_roles.cato) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "cato",
      });
    }
  }

  if (entityData?.claude_data?.role) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData.claude_data.role,
      source: IntegrationsMapping.claude.codeName,
    });
  }

  if (entityData?.admin_roles?.openai?.length > 0) {
    for (const role of entityData.admin_roles.openai) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "openai",
      });
    }
  }

  if (entityData?.admin_roles?.imperva_waf?.length > 0) {
    for (const role of entityData.admin_roles.imperva_waf) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "imperva_waf",
      });
    }
  }

  // Show OpenAI role if it exists and is not already shown as admin role
  if (
    entityData?.openai_data?.role &&
    !entityData?.admin_roles?.openai?.includes(entityData.openai_data.role)
  ) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData.openai_data.role,
      source: "openai",
    });
  }

  if (entityData?.atlassian_data?.platform_roles) {
    for (const role of entityData.atlassian_data.platform_roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "atlassian",
      });
    }
  }

  if (entityData?.zoom_data?.role_name) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData?.zoom_data?.role_name,
      source: "zoom",
    });
  }

  if (entityData?.starburst_data?.roles) {
    for (const role of entityData.starburst_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "starburst",
      });
    }
  }
  if (entityData?.ninjaone_data?.roles) {
    for (const role of entityData.ninjaone_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "ninjaone",
      });
    }
  }
  if (entityData?.zendesk_data?.platform_roles) {
    for (const role of entityData.zendesk_data.platform_roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "zendesk",
      });
    }
  }
  if (entityData?.jumpcloud_data?.role) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData?.jumpcloud_data?.role,
      source: "jumpcloud",
    });
  }
  if (entityData?.gitlab_data?.access_level) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData?.gitlab_data?.access_level,
      source: "gitlab",
    });
  }

  if (entityData?.crowdstrike_data?.roles) {
    for (const role of entityData.crowdstrike_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "crowdstrike",
      });
    }
  }

  if (entityData?.cloudconnexa_open_vpn_data?.role) {
    rowsMap.push({
      id: rowsMap.length + 1,
      role_name: entityData?.cloudconnexa_open_vpn_data?.role,
      source: "cloudconnexa_open_vpn",
    });
  }

  if (entityData?.paloalto_cortex_xdr_data?.roles) {
    for (const [
      index,
      role,
    ] of entityData?.paloalto_cortex_xdr_data.roles.entries()) {
      rowsMap.push({
        id: `${rowsMap.length + 1}-${index}`,
        role_name: role,
        source: "paloalto_cortex_xdr",
      });
    }
  }

  if (entityData?.cyberark_identity_data?.roles) {
    for (const role of entityData.cyberark_identity_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "cyberark_identity",
      });
    }
  }

  if (entityData?.imperva_waf_data?.roles) {
    for (const role of entityData.imperva_waf_data.roles) {
      rowsMap.push({
        id: rowsMap.length + 1,
        role_name: role,
        source: "imperva_waf",
      });
    }
  }

  return rowsMap;
};

export const checkIfUserAdmin = (entityData) => {
  if (entityData?.admin_roles && typeof entityData.admin_roles === "object") {
    return Object.values(entityData.admin_roles).some(
      (roles) => Array.isArray(roles) && roles.length > 0
    );
  }

  return false;
};
